import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { firebaseConnect } from "react-redux-firebase";
import { Aggregator } from "../commons/zones";
import Input from "../../forms/input";
import Carousel from "../commons/carousel";
import ProductCard from "./product-card";
import GMap from "../commons/gmap";
import Graph, { options, colors } from "../commons/graph";
import { withRouter } from "react-router-dom";
import { addToCart } from "../../store/actions/cart-actions";
import { useSetScrollToZero } from "../../utility/use-set-scroll-to-zero";
import { formatCurrency } from "../../utility/format-currency";
import { formatDocumentName } from "../../utility/format-document-name";
import { setUrlVar } from "../../utility/set-url-var";
import { getUrlVars } from "../../utility/get-url-vars";
import { VariationSelect } from "./utils";
import HighlightSeal from "../commons/highlight-seal";
import { useResources } from "../../utility/use-resources";
import ProductStock from "./product-stock";
import { Helmet } from 'react-helmet'
import { getViewport } from "../../utility/get-viewport";
import { PortfolioCardWrapper } from "../portfolio/portfolio-card";
import HTMLContent from "../commons/html-content";
import { NewsLineContainer } from "../news/news-line";

const buildDocumentDownloadUrl = ({ id, index }) =>
  "/download/products/" + id + "/doc/" + index;

const Variation = (props) => {
  return (
    <div className="product-variation">
      <div className="image-holder">
        <img
          src={
            props.images &&
            props.images[0] &&
            getViewport(props.images[0].downloadURL, 'xl')
          }
        />
      </div>
      <div className="product-reference">
        {props.unitsPerBox || props.specs ? (
          <p className="small">
            {props.unitsPerBox
              ? props.unitsPerBox
              : props.specs.reduce(
                  (acc, specList) =>
                    specList.list.reduce(
                      (_acc, spec) =>
                        spec.specName == "Units/Box" && spec.spec
                          ? _acc + parseInt(spec.spec)
                          : _acc,
                      acc
                    ),
                  0
                )}{" "}
            Units
          </p>
        ) : null}
        {props.eanCode && <p className="small">EAN: {props.eanCode}</p>}
        {props.reference && (
          <p className="small">Article: {props.reference}</p>
        )}
      </div>
      {props.children}
    </div>
  );
};

const ProductHOC = ({ variates, variations, ...props }) => {
  const urlVars = getUrlVars();
  const [higherGroup, setHigherGroup] = useState(urlVars.g || 0);
  const updateHigherGroup = (val) => {
    setHigherGroup((prev) => {
      if (variates.length > 0) {
        variates[prev].list.forEach(item => {
          setUrlVar(item.id, null)
        })
      }
      return val
    })
  }
  useSetScrollToZero(props.id);
  useEffect(() => {
    setUrlVar("g", higherGroup);
  }, [higherGroup]);
  return (
    <Product
      key={higherGroup}
      variates={variates.length > 0 ? variates[higherGroup].list : []}
      variations={
        variates.length > 0
          ? variations[higherGroup].list
          : {
              _defaults: {},
              index: [],
              store: {},
            }
      }
      variatingGroupSwitcher={
        variates.length > 1 ? (
          <li className="product-variation-group-selector">
            {variates.map((v, idx) => (
              <button
                onClick={() => updateHigherGroup(idx)}
                className={idx == higherGroup ? "active" : null}
              >
                {v.name}
              </button>
            ))}
          </li>
        ) : null
      }
      {...props}
    />
  );
};

const Product = (props) => {
  const [r] = useResources("general", "quoteButton");
  const quoteButton = {
    url: r("url") || "",
    name: r("name"),
  };

  const getDefaultForVariate = (variate) => {
    const search = props.history.location.search;
    if (search != "") {
      const searchObj = getUrlVars();
      if (
        searchObj.hasOwnProperty(variate.id) &&
        variate.list.indexOf(searchObj[variate.id]) != -1
      ) {
        return searchObj[variate.id];
      }
    }
    return props.variations._defaults[variate.id];
  };

  const [state, setState] = useState(
    props.variates.reduce(
      (acc, v) =>
        Object.assign({}, acc, {
          [v.id]: getDefaultForVariate(v),
        }),
      {}
    )
  );

  useEffect(() => {
    props.variates.forEach((key) => {
      setUrlVar(encodeURIComponent(key.id), encodeURIComponent(state[key.id]));
    });
  }, [state]);
  useEffect(
    () => () => {
      props.variates.forEach((v) => {
        setUrlVar(encodeURIComponent(v.id), null);
      });
    },
    []
  );

  let uid =
    props.variates.length > 0
      ? Object.values(state).reduce((acc, q) => acc + "_" + q)
      : "_default";

  let lineChartData = null;
  if (
    props.graphic &&
    props.graphic.length > 0 &&
    props.graphic[0].list.length > 0
  ) {
    lineChartData = {
      labels: props.graphic[0].list.map((i) => i.label),
      datasets: props.graphic.map((dataset, idx) =>
        ({
          ...options,
          borderColor: colors[idx],
          pointBackgroundColor: colors[idx],
          backgroundColor: colors[idx],
          label: dataset.name,
          data: dataset.list.map((val) => parseFloat(val.data)),
          spanGaps: true,
        })
      ),
    };
  }
  let secondaryLineChartData = null;
  if (
    props.secondaryGraphic &&
    props.secondaryGraphic.length > 0 &&
    props.secondaryGraphic[0].list.length > 0
  ) {
    secondaryLineChartData = {
      labels: props.secondaryGraphic[0].list.map((i) => i.label),
      datasets: props.secondaryGraphic.map((dataset, idx) =>
        ({
          ...options,
          borderColor: colors[idx],
          pointBackgroundColor: colors[idx],
          backgroundColor: colors[idx],
          label: dataset.name,
          data: dataset.list.map((val) => parseFloat(val.data)),
          spanGaps: true,
        })
      ),
    };
  }

  const getCurrentVariation = () => {
    let result = { ...props.variations };
    let idx = 0;
    while (
      result &&
      result.hasOwnProperty("index") &&
      result.index.length > 0
    ) {
      result = result.store[state[props.variates[idx].id]];
      idx++;
    }
    return !result || (result.index && result.index.length == 0)
      ? {
          prices: {},
        }
      : result;
  };

  const currentVariation = getCurrentVariation();

  const currentProduct = {
    ...props,
    ...currentVariation,
    discontinued: props.discontinued || currentVariation.discontinued,
    prices: {
      recommended:
        currentVariation.prices.recommended || props.prices.recommended,
      self: currentVariation.prices.self || props.prices.self,
    },
  };
  const hasVariationImages = !!currentVariation.images;

  quoteButton.url = quoteButton.url
    .replace("{{name}}", currentProduct.name)
    .replace("{{reference}}", currentProduct.reference)
    .replace(
      "{{variations}}",
      props.variates.map((key) => state[key.id]).join(", ")
    );
  const minQuantity = parseInt(currentProduct.minQuantity || 1)
  const [quantity, setQuantity] = useState(minQuantity);

  const changeQuantity = val => {
    setQuantity(
      Math.max(
        parseInt(val),
        parseInt(1),
      )
    )
  }
  const hasMinQuantity = minQuantity !== 1
  const isButtonDisabled = hasMinQuantity && minQuantity > parseInt(quantity || 0)
  const quantityLabel = `Quantity (BOXES)${hasMinQuantity ? ` (min. ${minQuantity})` : ''}`
  const blurQuantity = () => {
    setQuantity(
      Math.max(
        parseInt(quantity || 0),
        minQuantity,
      )
    )
  }

  const buyButton = !currentProduct.disableAddToCart
    ? (
      <button
        disabled={isButtonDisabled}
        onClick={() => props.addToCart(currentProduct, quantity)}
        className="link-primary link-outline link-dark add-to-cart"
      >
        {props.auth.uid ? "Add to basket" : "Add to Quote"}
      </button>
    )
    : (
      <a
        className="link-primary link-dark add-to-cart"
        target="_blank"
        href={quoteButton.url}
      >
        {quoteButton.name}
      </a>
    )

  return (
    <main id="product-page">
      <Helmet>
        <title>{props.name} - Vicoustic</title>
        <link rel="canonical" href={`https://vicoustic.com/product/${props.id}`} />
      </Helmet>
      <section className="product-main">
        <Aggregator>
          <div className="product-info">
            <div className="product-title">
              <h1 className="accent-text">
                {props.name}
                {!!props.nameSuffix && <span style={{ display: 'inline', fontSize: '80%', fontWeight: 400 }}>{` ${props.nameSuffix}`}</span>}
              </h1>
              <h2 className="h6">
                <b>{props.shortSummary}</b>
              </h2>
              {!currentProduct.disableAddToCart && !currentProduct.discontinued && <ProductStock reference={currentProduct.reference} />}
              <div className="product-prices">
                {
                  props.auth.uid &&
                  !currentProduct.discontinued &&
                  currentProduct.prices.self != null &&
                  currentProduct.prices.self != undefined ? (
                    <p className="accent-text self-price h5">
                      {formatCurrency(currentProduct.prices.self)}
                    </p>
                  ) : null}
                  {
                    props.auth.uid &&
                    !currentProduct.discontinued &&
                    currentProduct.prices.recommended != null &&
                    currentProduct.prices.recommended != undefined &&
                    currentProduct.prices.recommended !== currentProduct.prices.self ? (
                      <p className="recommended-price h5">
                        {formatCurrency(currentProduct.prices.recommended)} 
                        <abbr title="Suggested Retail Price">SRP</abbr>
                      </p>
                  ) : null}
                </div>
            </div>
            <ul className="product-variation-selector">
              {props.variatingGroupSwitcher}
              <VariationSelect
                variations={props.variations}
                variates={props.variates}
                idx={0}
                state={state}
                setState={setState}
              />
              <li>
                <div className="product-quantity">
                  {!currentProduct.discontinued ? (
                    <Input
                      type="number"
                      value={quantity}
                      onBlur={blurQuantity}
                      onChange={changeQuantity}
                      label={quantityLabel}
                    />
                  ) : null}
                  {!currentProduct.discontinued && buyButton}
                </div>
                {!props.auth.uid && !!currentProduct.shopUrl && (
                  <div data-theme="public-spaces" className="product-shop">
                    <a href={currentProduct.shopUrl} target="_blank" className="link-primary">
                      Shop now
                    </a>
                    <p className="small">In select EU countries</p>
                  </div>
                )}
              </li>
            </ul>
          </div>
          <Variation
            key={uid}
            {...props} // default product props
            {...currentProduct} // override product props
          >
            {(!!currentProduct.sealImages?.length || currentProduct.discontinued) && (
              <HighlightSeal
                images={
                  currentProduct.discontinued 
                    ? [{ downloadURL: '/resources/images/discontinued.png' }]
                    : currentProduct.sealImages
                }
                url={currentProduct.sealLink || ""}
              />
            )}
            {!!currentProduct.ctaSealImages?.length && (
              <HighlightSeal bottom images={currentProduct.ctaSealImages} url={currentProduct.ctaSealLink || ""} />
            )}
          </Variation>
        </Aggregator>
        <Aggregator>
          <aside className="product-aside">
            <ul className="product-highlights">
              {props.highlights.map((highlight, idx) => {
                return (
                  <li key={idx}>
                    <h6 className={idx % 2 != 0 ? "accent-text" : ""}>
                      <b>{highlight}</b>
                    </h6>
                  </li>
                );
              })}
            </ul>
            {props.documents && props.documents.length > 0 ? (
              <ul className="product-documents">
                {props.documents.map((doc, idx) => {
                  if (doc.thumbnail?.length) {
                    return (
                      <li key={idx} className="preview-doc">
                        <a
                          href={buildDocumentDownloadUrl({
                            id: props.id,
                            index: idx,
                          })}
                          target="_blank"
                        >
                          <img src={doc.thumbnail[0].downloadURL} />
                          <div className="document-name">
                            {doc.displayName ? doc.displayName : formatDocumentName(doc.ref)}
                          </div>
                        </a>
                      </li>
                    )
                  }
                  return (
                    <li key={idx}>
                      <a
                        href={buildDocumentDownloadUrl({
                          id: props.id,
                          index: idx,
                        })}
                        target="_blank"
                      >
                        {doc.ref.indexOf('.pdf') != -1 ? <i className="far fa-file-pdf"></i> : <i className="far fa-file"></i>}
                        {doc.displayName ? doc.displayName : formatDocumentName(doc.ref)}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {props.documentLinks && props.documentLinks.length > 0 ? (
              <ul className="product-documents">
                {props.documentLinks.map((doc, idx) => {
                  if (doc.thumbnail?.length) {
                    return (
                      <li key={idx} className="preview-doc">
                        <a
                          href={doc.downloadURL}
                          target="_blank"
                        >
                          <img src={doc.thumbnail[0].downloadURL} />
                          <div className="document-name">
                            {doc.displayName ? doc.displayName : formatDocumentName(doc.ref)}
                          </div>
                        </a>
                      </li>
                    )
                  }
                  return (
                    <li key={idx}>
                      <a
                        href={buildDocumentDownloadUrl({
                          id: props.id,
                          index: idx,
                        })}
                        target="_blank"
                      >
                        {doc.ref.indexOf('.pdf') != -1 ? <i className="far fa-file-pdf"></i> : <i className="far fa-file"></i>}
                        {doc.displayName ? doc.displayName : formatDocumentName(doc.ref)}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {props.images && props.images.length > 0 ? (
              <ul className="product-images">
                {props.images
                  .slice(hasVariationImages ? 0 : 1)
                  .map((img, idx) => {
                    return (
                      <li key={idx}>
                        <a href={img.downloadURL} target="_blank">
                          <img
                            alt={img.ref.split("/").pop()}
                            src={getViewport(img.downloadURL, 'xs')}
                          />
                        </a>
                      </li>
                    );
                  })}
              </ul>
            ) : null}
          </aside>
          <article className="product-description">
            <div className="product-summary content-description">
              <HTMLContent content={props.htmlContent} />
            </div>

            {lineChartData && (
              <Graph
                title={props.graphicTitle ? props.graphicTitle : "Acoustic Performance"}
                data={lineChartData}
              />
            )}
            {secondaryLineChartData && (
              <Graph
                title={props.secondaryGraphicTitle ? props.secondaryGraphicTitle : "Acoustic Performance"}
                data={secondaryLineChartData}
              />
            )}

            <ul className="product-specs">
              {props.specs.map((spec) => {
                return (
                  <li key={spec.name}>
                    <p className="spec-title small">{spec.name}</p>
                    <ul className="spec-list">
                      {spec.list.map((s) => {
                        return (
                          <li key={s.specName}>
                            <p className="small">
                              {s.specName + ": " + s.spec}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
            {props.relatedNews && props.relatedNews.length > 0 ? (
              <ul className="product-specs related-news">
                <li>
                  <p className="spec-title small">News about {props.name}</p>
                  <ul className="spec-list">
                    {props.relatedNews.map((newsId) => {
                      return <NewsLineContainer id={newsId} />;
                    })}
                  </ul>
                </li>
              </ul>
            ) : null}
            {props.disclaimers && props.disclaimers.length > 0 ? (
              <ul className="product-specs product-disclaimers">
                <li>
                  <p className="spec-title small">Disclaimers</p>
                  <ul className="spec-list">
                    {props.disclaimers.map((disc) => {
                      return (
                        <li key={disc}>
                          <p className="small">{disc}</p>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              </ul>
            ) : null}
            <div>
              {buyButton}
            </div>
          </article>
        </Aggregator>
        {props.related && props.related.length > 0 && (
          <div className="related-products">
            <Carousel title="People who like this, also like..." beStill={true}>
              {props.related.map((related) => (
                <ProductCard key={related} id={related} />
              ))}
            </Carousel>
          </div>
        )}
        {props.relatedProjects && props.relatedProjects.length > 0 && (
          <div className="related-products">
            <Carousel title={`Projects featuring ${props.name}`} beStill={true}>
              {props.relatedProjects.map((related) => (
                <PortfolioCardWrapper key={related} id={related} />
              ))}
            </Carousel>
          </div>
        )}
        <GMap />
      </section>
    </main>
  );
};

export default compose(
  withRouter,
  firebaseConnect(),
  connect(
    (state) => ({
      auth: state.firebase.auth,
    }),
    (dispatch) => ({
      addToCart: (product, quantity) => dispatch(addToCart(product, quantity)),
    })
  )
)(ProductHOC);
